import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  LIVE_COUNT_CAPTION,
  MEASUREMENT_IN_PROGRESS_LABEL,
  OBSERVED_CHANGE_QUALIFIER,
  OFFICIAL_COUNT_CAPTION,
  EXPLORATORY_RATE_CAPTION,
  QUALIFYING_ENTRY_HEADING,
  QUALIFYING_ENTRY_HELPER,
  QUALIFYING_ENTRY_METRIC,
  addSiteCalendarDays,
  measuredAreaLabel,
  observedChangeCopy,
  periodDisplayStatus,
  siteCalendarDay,
  type PeriodSummary,
  type QualifyingEntryActivityView,
  type QualifyingEntryBar,
  type QualifyingEntryParentBucket,
} from '../../domain/showcase/qualifyingEntryActivity';
import {
  BOOTH_MEASUREMENT_STATUS_LABEL,
  evidenceHeading,
  type BoothMeasurementStatus,
  type BoothRecentEvidenceItem,
  type BoothRecentEvidenceView,
} from '../../domain/showcase/boothRecentEvidence';
import { useShowcaseBoothRecentEvidence } from '../../hooks/useShowcaseBoothRecentEvidence';
import { useShowcaseQualifyingEntries } from '../../hooks/useShowcaseQualifyingEntries';
import { camerasApi } from '../../services/camerasApi';
import { zonesApi } from '../../services/zonesApi';

export const SHOWCASE_LIVE_ENTRY_COUNT_FONT = 56;
export const SHOWCASE_LIVE_REPEATING_CHART_HEIGHT = 148;
export const SHOWCASE_LIVE_REPEATING_BAR_COLOR = '#38bdf8';

const PROVISIONAL_STRIPE = 'repeating-linear-gradient(135deg, rgba(56,189,248,0.72) 0 5px, rgba(56,189,248,0.08) 5px 10px)';

function Bar({ bar, maxTotal }: { bar: QualifyingEntryBar; maxTotal: number }) {
  const total = bar.finalizedCount + bar.provisionalCount;
  const testId = `showcase-live-repeating-bar-${bar.key}`;
  const shared = {
    'data-testid': testId,
    'data-presentation': bar.presentation,
    'data-future': bar.future ? 'true' : 'false',
    'data-finalized-count': String(bar.finalizedCount),
    'data-provisional-count': String(bar.provisionalCount),
    'data-total': String(total),
    title: bar.label,
    'aria-label': bar.label,
    role: 'img' as const,
  };
  if (bar.presentation === 'future') {
    return <Box sx={{ flex: 1, minWidth: 0, height: '100%' }} {...shared} />;
  }
  if (bar.presentation === 'missing' || bar.presentation === 'open') {
    return (
      <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }} {...shared}>
        <Box sx={{ width: '100%', height: 3, borderTop: '1px dashed rgba(186, 230, 253, 0.35)', opacity: 0.7 }} />
      </Box>
    );
  }
  if (bar.presentation === 'finalized-zero') {
    return (
      <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }} {...shared}>
        <Box sx={{ width: '100%', height: 3, bgcolor: SHOWCASE_LIVE_REPEATING_BAR_COLOR, opacity: 0.4, borderRadius: 0.4 }} />
      </Box>
    );
  }
  const height = Math.max(12, Math.round((total / maxTotal) * 100));
  const finalizedPct = (bar.finalizedCount / total) * 100;
  const provisionalPct = (bar.provisionalCount / total) * 100;
  return (
    <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }} {...shared}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'flex-end' }}>
        <Box sx={{ width: '100%', height: `${height}%`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          {bar.provisionalCount > 0 ? (
            <Box
              data-testid={`${testId}-provisional`}
              data-treatment="pending"
              sx={{
                height: `${provisionalPct}%`,
                minHeight: 4,
                boxSizing: 'border-box',
                backgroundImage: PROVISIONAL_STRIPE,
                border: '2px solid #38bdf8',
                borderRadius: 0.4,
              }}
            />
          ) : null}
          {bar.finalizedCount > 0 ? (
            <Box
              data-testid={`${testId}-finalized`}
              data-treatment="solid"
              sx={{
                height: `${finalizedPct}%`,
                minHeight: 4,
                bgcolor: SHOWCASE_LIVE_REPEATING_BAR_COLOR,
                borderRadius: 0.4,
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function formatMeasured(value: number | null, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Number(value.toFixed(digits));
  return String(rounded);
}

function PeriodFacts({
  period,
  testKey,
  view,
}: {
  period: PeriodSummary;
  testKey: 'morning' | 'afternoon';
  view: QualifyingEntryActivityView;
}) {
  const title = testKey === 'morning' ? 'AM' : 'PM';
  const windowLabel = period.label || (testKey === 'morning' ? '12 AM–12 PM' : '12 PM–12 AM');
  const status = periodDisplayStatus(period, {
    nowMs: view.asOfMs,
    timeZone: view.timeZone,
    selectedLocalDay: view.selectedLocalDay,
    isToday: view.isToday,
    which: testKey,
  });
  const measured = status !== 'UNAVAILABLE' && period.count != null && period.usableHours != null && period.rate != null;
  return (
    <Box
      data-testid={`showcase-compare-${testKey}`}
      data-coverage={period.coverage}
      data-status={status}
      sx={{
        minWidth: 0,
        p: 1.25,
        borderRadius: 1.25,
        border: '1px solid rgba(56,189,248,0.45)',
        bgcolor: 'rgba(8, 20, 38, 0.72)',
      }}
    >
      <Typography sx={{ color: '#9fb0c8', fontSize: 13, fontWeight: 800, letterSpacing: 0.6 }}>
        {title} — {windowLabel}
      </Typography>
      {measured ? (
        <>
          <Typography data-testid={`showcase-compare-${testKey}-count`} sx={{ fontSize: 40, fontWeight: 800, lineHeight: 0.95, letterSpacing: -1, mt: 0.5 }}>
            {formatMeasured(period.count, Number.isInteger(period.count) ? 0 : 2)}
          </Typography>
          <Typography sx={{ color: '#c5d2e4', fontSize: 13, lineHeight: 1.2 }}>finalized, rate-eligible</Typography>
          <Typography data-testid={`showcase-compare-${testKey}-hours`} sx={{ color: '#c5d2e4', fontSize: 14, lineHeight: 1.3, mt: 0.75 }}>
            {formatMeasured(period.usableHours)} usable camera-hours
          </Typography>
          <Typography data-testid={`showcase-compare-${testKey}-rate`} sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25, mt: 0.25 }}>
            {formatMeasured(period.rate)} entries per usable camera-hour
          </Typography>
        </>
      ) : (
        <Typography data-testid={`showcase-compare-${testKey}-unavailable`} sx={{ color: '#c5d2e4', fontSize: 15, lineHeight: 1.3, mt: 1 }}>
          No admissible measurement for this period.
        </Typography>
      )}
      <Typography data-testid={`showcase-compare-${testKey}-coverage`} sx={{ color: '#7dd3fc', fontSize: 13, fontWeight: 800, letterSpacing: 0.6, mt: 0.75 }}>
        {status}
      </Typography>
    </Box>
  );
}

function PeriodComparisonPanel({ view }: { view: QualifyingEntryActivityView }) {
  const comparison = view.comparison;
  const morning = comparison?.morning;
  const afternoon = comparison?.afternoon;
  const change = observedChangeCopy(comparison);
  return (
    <Box data-testid="showcase-period-comparison" sx={{ mt: 1, flex: '0 0 auto', minWidth: 0 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1.25 }}>
        <PeriodFacts period={morning || { label: '12 AM–12 PM', count: null, usableHours: null, rate: null, coverage: 'unavailable', includedBucketCount: 0, basis: null, epoch: null }} testKey="morning" view={view} />
        <PeriodFacts period={afternoon || { label: '12 PM–12 AM', count: null, usableHours: null, rate: null, coverage: 'unavailable', includedBucketCount: 0, basis: null, epoch: null }} testKey="afternoon" view={view} />
      </Box>
      <Box
        data-testid="showcase-observed-change"
        sx={{ mt: 1.25, p: 1.25, borderRadius: 1.25, border: '1px solid rgba(255,255,255,0.14)', bgcolor: 'rgba(8, 20, 38, 0.55)', minWidth: 0 }}
      >
        <Typography sx={{ color: '#9fb0c8', fontSize: 12, fontWeight: 800, letterSpacing: 0.8 }}>
          PM vs AM · entry rate
        </Typography>
        {change.headline ? (
          <Typography data-testid="showcase-compare-change" sx={{ mt: 0.5, fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
            {change.headline}
          </Typography>
        ) : (
          <Typography data-testid="showcase-compare-unavailable" sx={{ mt: 0.5, color: '#c5d2e4', fontSize: 15, lineHeight: 1.35 }}>
            {change.unavailable}
          </Typography>
        )}
        <Typography data-testid="showcase-compare-note" sx={{ mt: 0.6, color: '#9fb0c8', fontSize: 13, lineHeight: 1.35 }}>
          {OBSERVED_CHANGE_QUALIFIER}
        </Typography>
      </Box>
    </Box>
  );
}

function MeasurementControls({
  view,
  compareOpen,
  onCompareOpenChange,
  onPreviousDay,
  onNextDay,
}: {
  view: QualifyingEntryActivityView;
  compareOpen: boolean;
  onCompareOpenChange: (open: boolean) => void;
  onPreviousDay?: () => void;
  onNextDay?: () => void;
}) {
  const segment = (active: boolean) => ({
    border: 0,
    cursor: 'pointer',
    px: 1.5,
    py: 0.9,
    fontSize: 15,
    fontWeight: 800,
    color: active ? '#082033' : '#e2e8f0',
    bgcolor: active ? '#38bdf8' : 'transparent',
  });
  return (
    <Box
      data-testid="showcase-measurement-controls"
      sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1, minWidth: 0 }}
    >
      <Box
        role="group"
        aria-label="Measurement view"
        sx={{ display: 'inline-flex', border: '1px solid #39738f', borderRadius: 1, overflow: 'hidden', flex: '0 0 auto' }}
      >
        <Box
          component="button"
          type="button"
          data-testid="showcase-hourly-activity"
          aria-pressed={!compareOpen}
          onClick={() => onCompareOpenChange(false)}
          sx={segment(!compareOpen)}
        >
          Hourly activity
        </Box>
        <Box
          component="button"
          type="button"
          data-testid="showcase-compare-toggle"
          aria-pressed={compareOpen}
          aria-expanded={compareOpen}
          onClick={() => onCompareOpenChange(true)}
          sx={segment(compareOpen)}
        >
          Compare periods
        </Box>
      </Box>
      <Box data-testid="showcase-date-nav" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Box
          component="button"
          type="button"
          data-testid="showcase-date-prev"
          aria-label="Previous day"
          onClick={onPreviousDay}
          sx={{ border: '1px solid #39738f', bgcolor: 'transparent', color: '#e2e8f0', borderRadius: 1, px: 1.1, py: 0.7, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
        >
          ‹
        </Box>
        <Typography data-testid="showcase-selected-date" sx={{ fontSize: 15, fontWeight: 750, px: 0.5, whiteSpace: 'nowrap' }}>
          {view.dateLabel || 'Today'}
        </Typography>
        <Box
          component="button"
          type="button"
          data-testid="showcase-date-next"
          aria-label="Next day"
          disabled={view.isToday}
          onClick={onNextDay}
          sx={{ border: '1px solid #39738f', bgcolor: 'transparent', color: view.isToday ? '#64748b' : '#e2e8f0', borderRadius: 1, px: 1.1, py: 0.7, fontSize: 18, cursor: view.isToday ? 'default' : 'pointer', lineHeight: 1 }}
        >
          ›
        </Box>
      </Box>
    </Box>
  );
}

export function ShowcaseQualifyingEntriesCard({
  view,
  compareOpen,
  onCompareOpenChange,
  onPreviousDay,
  onNextDay,
  part = 'all',
  labelled = true,
  measuredArea,
}: {
  view: QualifyingEntryActivityView;
  compareOpen?: boolean;
  onCompareOpenChange?: (open: boolean) => void;
  onPreviousDay?: () => void;
  onNextDay?: () => void;
  part?: 'all' | 'summary' | 'detail';
  labelled?: boolean;
  measuredArea?: string | null;
}) {
  const maxTotal = Math.max(...view.bars.map((bar) => bar.finalizedCount + bar.provisionalCount), 1);
  const [internalCompare, setInternalCompare] = useState(true);
  const compare = compareOpen ?? internalCompare;
  const setCompare = onCompareOpenChange || setInternalCompare;
  const showSummary = part !== 'detail';
  const showDetail = part !== 'summary';
  return (
    <Box
      data-testid={labelled ? 'showcase-live-repeating' : undefined}
      data-status={view.status}
      data-compare-open={compare ? 'true' : 'false'}
      sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      {showSummary ? (
      <>
      <Typography
        data-testid="showcase-live-repeating-heading"
        sx={{ fontWeight: 800, letterSpacing: 1.4, fontSize: 12, color: '#9fb0c8', lineHeight: 1 }}
      >
        {QUALIFYING_ENTRY_HEADING}
      </Typography>
      {measuredArea ? (
        <Typography data-testid="showcase-measured-area" sx={{ color: '#c5d2e4', fontSize: 13, mt: 0.45, lineHeight: 1.3 }}>
          {measuredArea}
        </Typography>
      ) : null}
      <Typography data-testid="showcase-live-repeating-metric" sx={{ fontWeight: 750, fontSize: 14, mt: 0.4, lineHeight: 1.2 }}>
        {QUALIFYING_ENTRY_METRIC}
      </Typography>
      {view.status === 'loading' ? (
        <Typography data-testid="showcase-live-repeating-loading" sx={{ color: '#9fb0c8', fontSize: 13, mt: 0.5 }}>
          Recent entries loading
        </Typography>
      ) : null}
      {view.status === 'error' ? (
        <>
          <Typography data-testid="showcase-live-repeating-error" sx={{ color: '#fbbf24', fontSize: 13, mt: 0.5 }}>
            Recent entries unavailable
          </Typography>
          <Typography data-testid="showcase-live-repeating-today" sx={{ fontWeight: 750, fontSize: 13, mt: 0.35 }}>
            {view.todayLabel}
          </Typography>
        </>
      ) : null}
      {view.status !== 'loading' && view.status !== 'error' ? (
        <>
          {view.measurementInProgress ? (
            <Typography data-testid="showcase-measurement-in-progress" sx={{ fontWeight: 800, fontSize: 22, letterSpacing: 0.2, mt: 0.45, lineHeight: 1.15 }}>
              {MEASUREMENT_IN_PROGRESS_LABEL}
            </Typography>
          ) : null}
          {view.prominentCount != null ? (
            <Box data-testid="showcase-prominent-count" data-kind={view.prominentKind}>
              <Typography
                data-testid={view.prominentKind === 'live-provisional' ? 'showcase-live-repeating-today-count' : undefined}
                sx={{ fontWeight: 800, fontSize: SHOWCASE_LIVE_ENTRY_COUNT_FONT, lineHeight: 0.9, letterSpacing: -1.6, mt: 0.2 }}
              >
                {view.prominentCount}
              </Typography>
              <Typography data-testid="showcase-prominent-caption" sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.6, mt: 0.2 }}>
                {view.prominentCaption}
              </Typography>
            </Box>
          ) : null}
          {view.prominentKind === 'finalized' && view.todayCount != null ? (
            <Typography
              data-testid="showcase-live-repeating-today-count"
              sx={{ fontWeight: 750, fontSize: 28, lineHeight: 1, letterSpacing: -0.6, mt: 0.6 }}
            >
              {view.todayCount}
            </Typography>
          ) : null}
          <Typography data-testid="showcase-live-repeating-today" sx={{ fontWeight: 750, fontSize: 16, mt: 0.1, lineHeight: 1.15 }}>
            {view.todayCount != null ? LIVE_COUNT_CAPTION : view.todayLabel}
            {view.todayCount != null ? (
              <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
                {view.todayLabel}
              </Box>
            ) : null}
          </Typography>
          {view.countDistinction ? (
            <Typography data-testid="showcase-count-distinction" sx={{ color: '#9fb0c8', fontSize: 12, mt: 0.25, lineHeight: 1.2 }}>
              {view.countDistinction}
            </Typography>
          ) : null}
          {view.identityUnverifiedCount > 0 ? (
            <Typography
              data-testid="showcase-live-repeating-identity-unverified"
              data-count={String(view.identityUnverifiedCount)}
              sx={{ color: '#fbbf24', fontSize: 13, mt: 0.35, lineHeight: 1.2 }}
            >
              Identity unverified
            </Typography>
          ) : null}
          {view.boothHoursLabel ? (
            <Typography data-testid="showcase-live-repeating-booth-hours" sx={{ color: '#9fb0c8', fontSize: 12, mt: 0.45, lineHeight: 1.2 }}>
              {view.boothHoursLabel}
            </Typography>
          ) : null}
          {view.dateLabel ? (
            <Typography data-testid="showcase-live-repeating-day" sx={{ color: '#c5d2e4', fontSize: 14, mt: 0.35, lineHeight: 1.2 }}>
              {view.dateLabel}
            </Typography>
          ) : null}
        </>
      ) : null}
      </>
      ) : null}
      {showDetail && view.status !== 'loading' && view.status !== 'error' ? (
        <>
          {view.officialFinalizedCount != null ? (
            <Typography data-testid="showcase-official-finalized-count" sx={{ fontWeight: 750, fontSize: 18, lineHeight: 1.1, mt: 0.4 }}>
              {view.officialFinalizedCount}
            </Typography>
          ) : (
            <Typography data-testid="showcase-official-finalized-count" sx={{ fontWeight: 750, fontSize: 18, mt: 0.4 }}>
              Finalized count unavailable
            </Typography>
          )}
          <Typography data-testid="showcase-official-finalized-caption" sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.6, mt: 0.2 }}>
            {OFFICIAL_COUNT_CAPTION}
          </Typography>
          {view.exploratoryRateLabel ? (
            <Typography data-testid="showcase-exploratory-operational-rate" data-caption={EXPLORATORY_RATE_CAPTION} sx={{ color: '#fbbf24', fontSize: 13, fontWeight: 700, mt: 0.45, lineHeight: 1.3 }}>
              {view.exploratoryRateLabel}
            </Typography>
          ) : null}
          <Typography data-testid="showcase-live-repeating-through" sx={{ color: '#9fb0c8', fontSize: 13, mt: 0.35 }}>
            {view.updatedThroughLabel}
          </Typography>
          {view.historyDelayedLabel ? (
            <Typography data-testid="showcase-history-delayed" sx={{ color: '#fbbf24', fontSize: 15, fontWeight: 800, mt: 0.6, lineHeight: 1.3 }}>
              {view.historyDelayedLabel}
            </Typography>
          ) : null}
          <Box
            data-testid="showcase-live-repeating-chart"
            data-chart-height={String(SHOWCASE_LIVE_REPEATING_CHART_HEIGHT)}
            data-now-percent={String(view.nowMarkerPercent)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              gap: 0.45,
              flex: '1 1 auto',
              minHeight: 64,
              height: SHOWCASE_LIVE_REPEATING_CHART_HEIGHT,
              maxHeight: SHOWCASE_LIVE_REPEATING_CHART_HEIGHT,
              mt: 0.35,
            }}
          >
            {view.bars.map((bar) => (
              <Bar key={bar.key} bar={bar} maxTotal={maxTotal} />
            ))}
            {view.showNowMarker ? (
              <Box
                data-testid="showcase-live-repeating-now"
                data-now-percent={String(view.nowMarkerPercent)}
                sx={{
                  position: 'absolute',
                  left: `${view.nowMarkerPercent}%`,
                  top: 0,
                  bottom: 0,
                  width: '2px',
                  bgcolor: '#e2e8f0',
                  transform: 'translateX(-1px)',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              />
            ) : null}
          </Box>
          <Box data-testid="showcase-live-repeating-axis" sx={{ position: 'relative', height: 16, mt: 0.25, flex: '0 0 auto' }}>
            {view.axisLabels.map((tick) => (
              <Typography
                key={tick.key}
                data-testid={`showcase-live-repeating-axis-${tick.key}`}
                data-percent={String(tick.percent)}
                sx={{
                  position: 'absolute',
                  left: `${tick.percent}%`,
                  top: 0,
                  transform: tick.percent <= 0 ? 'none' : tick.percent >= 100 ? 'translateX(-100%)' : 'translateX(-50%)',
                  fontSize: 10,
                  lineHeight: 1.1,
                  color: '#8ea0b8',
                  whiteSpace: 'nowrap',
                }}
              >
                {tick.label}
              </Typography>
            ))}
          </Box>
          <Typography data-testid="showcase-live-repeating-legend" sx={{ color: '#9fb0c8', fontSize: 11, mt: 0.2, lineHeight: 1.2, flex: '0 0 auto' }}>
            Solid = finalized · Hatched = pending finalization
          </Typography>
          <MeasurementControls
            view={view}
            compareOpen={compare}
            onCompareOpenChange={setCompare}
            onPreviousDay={onPreviousDay}
            onNextDay={onNextDay}
          />
          {compare ? <PeriodComparisonPanel view={view} /> : null}
          <Typography data-testid="showcase-live-repeating-helper" sx={{ color: '#8ea0b8', fontSize: 11, mt: 0.4, lineHeight: 1.3 }}>
            {QUALIFYING_ENTRY_HELPER}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

function measurementStatusFor(
  item: BoothRecentEvidenceItem,
  buckets: QualifyingEntryParentBucket[],
): BoothMeasurementStatus {
  const at = Date.parse(item.eventTime);
  const bucket = buckets.find((row) => {
    const start = Date.parse(row.bucketStart);
    const end = Date.parse(row.bucketEnd);
    return Number.isFinite(start) && Number.isFinite(end) && start <= at && at < end;
  });
  if (!bucket || bucket.finality !== 'finalized') return 'pending';
  const outcome = String(bucket.coverageOutcome || '').trim().toLowerCase();
  if (outcome === 'partial_claim_limited') return 'finalized-limited';
  if (outcome === 'sufficient') return 'finalized-measured';
  return 'pending';
}

function EvidenceRows({
  rows,
  buckets,
}: {
  rows: BoothRecentEvidenceItem[];
  buckets: QualifyingEntryParentBucket[];
}) {
  return (
    <Box sx={{ mt: 0.25 }}>
      {rows.map((item) => {
        const measurement = measurementStatusFor(item, buckets);
        return (
          <Box
            key={item.episodeId}
            data-testid={`showcase-booth-recent-evidence-row-${item.episodeId}`}
            data-evidence-state={item.evidenceState}
            data-measurement-status={measurement}
            sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.45, minWidth: 0, fontSize: 14, lineHeight: 1.45 }}
          >
            <Box component="span" sx={{ color: '#c5d2e4', whiteSpace: 'nowrap' }}>{item.timeLabel}</Box>
            <Box component="span" sx={{ color: '#f8fafc', whiteSpace: 'nowrap' }}>Restricted-area entry</Box>
            <Box component="span" sx={{ color: '#9fb0c8', whiteSpace: 'nowrap' }}>{item.zoneLabel}</Box>
            {item.durationMs != null ? (
              <Box component="span" data-testid={`showcase-booth-recent-evidence-duration-${item.episodeId}`} sx={{ color: '#9fb0c8', whiteSpace: 'nowrap' }}>
                {(item.durationMs / 1000).toFixed(item.durationMs % 1000 === 0 ? 0 : 1)}s
              </Box>
            ) : null}
            <Box component="span" data-testid={`showcase-booth-recent-evidence-measurement-${item.episodeId}`} sx={{ color: '#7dd3fc', fontSize: 13, whiteSpace: 'nowrap' }}>
              {BOOTH_MEASUREMENT_STATUS_LABEL[measurement]}
            </Box>
            {item.href ? (
              <Box
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`showcase-booth-recent-evidence-link-${item.episodeId}`}
                sx={{ color: '#60a5fa', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', ml: 'auto', textDecoration: 'none' }}
              >
                {item.stateLabel}
              </Box>
            ) : (
              <Box
                component="span"
                data-testid={`showcase-booth-recent-evidence-state-${item.episodeId}`}
                sx={{ color: '#9fb0c8', fontSize: 13, whiteSpace: 'nowrap', ml: 'auto' }}
              >
                {item.stateLabel}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function ShowcaseBoothRecentEvidenceCard({
  view,
  buckets = [],
}: {
  view: BoothRecentEvidenceView;
  buckets?: QualifyingEntryParentBucket[];
}) {
  const [showAll, setShowAll] = useState(false);
  const recorded = view.recordedCount ?? view.items.length;
  const shown = Math.min(2, view.items.length, recorded);
  const allRows = (view.occurrences && view.occurrences.length > 0 ? view.occurrences : view.items);
  return (
    <Box
      data-testid="showcase-booth-recent-evidence"
      data-status={view.status}
      sx={{ mt: 1, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.10)', minWidth: 0 }}
    >
      <Typography data-testid="showcase-booth-recent-evidence-heading" sx={{ fontWeight: 800, letterSpacing: 0.4, fontSize: 12, color: '#9fb0c8', lineHeight: 1.3 }}>
        {evidenceHeading(recorded, shown)}
      </Typography>
      <Typography data-testid="showcase-booth-recent-evidence-hours" sx={{ color: '#9fb0c8', fontSize: 12, mt: 0.25, lineHeight: 1.2 }}>
        Selected day
      </Typography>
      {view.status === 'loading' ? (
        <Typography data-testid="showcase-booth-recent-evidence-loading" sx={{ color: '#9fb0c8', fontSize: 13, mt: 0.35 }}>
          Loading recent events…
        </Typography>
      ) : view.status === 'empty' ? (
        <Typography data-testid="showcase-booth-recent-evidence-empty" sx={{ color: '#9fb0c8', fontSize: 13, mt: 0.35 }}>
          No completed entries yet.
        </Typography>
      ) : view.status === 'unavailable' ? (
        <Typography data-testid="showcase-booth-recent-evidence-unavailable" sx={{ color: '#9fb0c8', fontSize: 13, mt: 0.35 }}>
          Evidence temporarily unavailable.
        </Typography>
      ) : (
        <>
          <EvidenceRows rows={view.items.slice(0, 2)} buckets={buckets} />
          <Box
            component="button"
            type="button"
            data-testid="showcase-view-all-evidence"
            onClick={() => setShowAll((open) => !open)}
            sx={{ mt: 0.6, border: 0, bgcolor: 'transparent', color: '#7dd3fc', fontWeight: 800, fontSize: 13, cursor: 'pointer', px: 0 }}
          >
            View all evidence
          </Box>
          {showAll ? (
            <Box data-testid="showcase-all-evidence" sx={{ mt: 0.5 }}>
              <EvidenceRows rows={allRows} buckets={buckets} />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
}

function useMeasuredAreaNames(args: {
  siteId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
}): { label: string; zoneName: string | null; cameraName: string | null } {
  const siteId = String(args.siteId || '').trim();
  const deviceId = String(args.deviceId || '').trim();
  const cameraId = String(args.cameraId || '').trim();
  const zoneId = String(args.zoneId || '').trim();
  const [names, setNames] = useState<{ zoneName: string | null; cameraName: string | null }>({
    zoneName: null,
    cameraName: null,
  });
  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setNames({ zoneName: null, cameraName: null });
      return undefined;
    }
    const load = async () => {
      let zoneName: string | null = null;
      let cameraName: string | null = null;
      try {
        const zones = await zonesApi.listZones(undefined, undefined, deviceId || undefined, cameraId || undefined, undefined, undefined, undefined, siteId);
        const zone = (zones?.zones || []).find((row) => String(row.zone_id || row.id || '') === zoneId);
        zoneName = String(zone?.name || '').trim() || null;
      } catch {
        zoneName = null;
      }
      try {
        const cameras = await camerasApi.listCameras(siteId, deviceId || undefined);
        const camera = (cameras?.items || []).find((row) => String(row.camera_id || '') === cameraId);
        cameraName = String(camera?.name || '').trim() || null;
      } catch {
        cameraName = null;
      }
      if (!cancelled) setNames({ zoneName, cameraName });
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [cameraId, deviceId, siteId, zoneId]);
  return { label: measuredAreaLabel(names), zoneName: names.zoneName, cameraName: names.cameraName };
}

export function ShowcaseBoothRecentEvidence(props: {
  siteId?: string | null;
  conditionId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  policyId?: string | null;
  timeZone?: string | null;
  selectedLocalDay?: string | null;
  zoneLabel?: string | null;
  cameraLabel?: string | null;
  buckets?: QualifyingEntryParentBucket[];
}) {
  const view = useShowcaseBoothRecentEvidence(props);
  return <ShowcaseBoothRecentEvidenceCard view={view} buckets={props.buckets} />;
}

export function ShowcaseQualifyingEntries(props: {
  enabled: boolean;
  recentEvidenceEnabled?: boolean;
  matchingIncidentId?: string | null;
  siteId?: string | null;
  conditionId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  policyId?: string | null;
  timeZone?: string | null;
}) {
  const [selectedLocalDay, setSelectedLocalDay] = useState<string | null>(null);
  const [compareOpen, setCompareOpen] = useState(true);
  const view = useShowcaseQualifyingEntries({ ...props, selectedLocalDay });
  const measured = useMeasuredAreaNames(props);
  if (!props.enabled) return null;
  const currentDay = view.selectedLocalDay;
  const shiftDay = (delta: number) => {
    if (!currentDay) return;
    const today = siteCalendarDay(new Date(view.asOfMs || Date.now()).toISOString(), view.timeZone);
    const next = addSiteCalendarDays(currentDay, delta);
    if (!today || next >= today) setSelectedLocalDay(null);
    else setSelectedLocalDay(next);
  };
  const shared = {
    view,
    compareOpen,
    onCompareOpenChange: setCompareOpen,
    onPreviousDay: () => shiftDay(-1),
    onNextDay: () => shiftDay(1),
  };
  return (
    <Box data-testid="showcase-live-repeating" data-status={view.status} data-compare-open={compareOpen ? 'true' : 'false'} sx={{ display: 'contents' }}>
      <Box sx={{ gridColumn: '2', gridRow: '1', minWidth: 0, alignSelf: 'start' }}>
        <ShowcaseQualifyingEntriesCard {...shared} part="summary" labelled={false} measuredArea={measured.label} />
      </Box>
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <ShowcaseQualifyingEntriesCard {...shared} part="detail" labelled={false} />
      </Box>
      {props.recentEvidenceEnabled ? (
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <ShowcaseBoothRecentEvidence
            siteId={props.siteId}
            conditionId={props.conditionId}
            deviceId={props.deviceId}
            cameraId={props.cameraId}
            zoneId={props.zoneId}
            policyId={props.policyId}
            timeZone={props.timeZone}
            selectedLocalDay={view.selectedLocalDay}
            zoneLabel={measured.zoneName}
            cameraLabel={measured.cameraName}
            buckets={view.buckets}
          />
        </Box>
      ) : null}
    </Box>
  );
}
